import { useState, useCallback } from 'react';
import { usePermission, type PermissionModule, type PermissionOperation } from './usePermission';

export interface QuickAccessTarget {
  id: string;
  path: string;
  labelKey: string;
  iconName: string;
  category: 'nav' | 'create' | 'integration' | 'tools';
  /**
   * Recht, ohne das der Link weder angeboten noch angezeigt wird (V-1585). Ein Schnellzugriff auf eine
   * Seite, die der Benutzer nicht öffnen darf, führt nur zu einer Absage — „Berichte" verlor der Vertrieb
   * mit `reports.view` und damit auch den Link.
   */
  requiredPermission?: { module: string; operation: string };
}

export const QUICK_ACCESS_TARGETS: QuickAccessTarget[] = [
  // Navigation
  { id: 'articles', path: '/articles', labelKey: 'nav.articles', iconName: 'Package', category: 'nav' },
  { id: 'orders', path: '/orders', labelKey: 'nav.orders', iconName: 'ShoppingCart', category: 'nav' },
  { id: 'inventory', path: '/inventory', labelKey: 'nav.inventory', iconName: 'Warehouse', category: 'nav' },
  { id: 'shipping', path: '/shipping', labelKey: 'nav.shipping', iconName: 'Truck', category: 'nav' },
  { id: 'crm', path: '/crm', labelKey: 'nav.crm', iconName: 'Users', category: 'nav' },
  { id: 'accounting', path: '/accounting', labelKey: 'nav.accounting', iconName: 'Receipt', category: 'nav' },
  { id: 'purchasing', path: '/purchasing', labelKey: 'nav.purchasing', iconName: 'Building2', category: 'nav' },
  { id: 'reports', path: '/reports', labelKey: 'nav.reports', iconName: 'BarChart3', category: 'nav', requiredPermission: { module: 'reports', operation: 'view' } },
  { id: 'tasks', path: '/tasks', labelKey: 'nav.tasks', iconName: 'CheckSquare', category: 'nav' },
  { id: 'listings', path: '/listings', labelKey: 'nav.listings', iconName: 'Store', category: 'nav' },
  { id: 'settings', path: '/settings', labelKey: 'nav.settings', iconName: 'Settings', category: 'nav' },
  { id: 'returns', path: '/returns', labelKey: 'nav.returns', iconName: 'RotateCcw', category: 'nav' },

  // Create Actions
  { id: 'articles-new', path: '/articles?action=new', labelKey: 'dashboard.newArticle', iconName: 'PackagePlus', category: 'create' },
  { id: 'orders-new', path: '/orders?action=new', labelKey: 'dashboard.newOrder', iconName: 'ShoppingCart', category: 'create' },
  { id: 'inventory-receiving', path: '/inventory?tab=receiving', labelKey: 'nav.goodsReceipt', iconName: 'PackageCheck', category: 'create' },
  { id: 'inventory-stocktake', path: '/inventory?tab=stocktake', labelKey: 'dashboard.startStocktake', iconName: 'ClipboardCheck', category: 'create' },
  { id: 'customers-new', path: '/crm?action=new', labelKey: 'dashboard.quickAccess.newCustomer', iconName: 'UserPlus', category: 'create' },
  { id: 'invoice-new', path: '/accounting?action=new-invoice', labelKey: 'dashboard.quickAccess.newInvoice', iconName: 'FileText', category: 'create' },

  // Integrations
  { id: 'ebay', path: '/settings/ebay', labelKey: 'dashboard.quickAccess.ebay', iconName: 'Store', category: 'integration' },
  { id: 'amazon', path: '/settings/amazon', labelKey: 'dashboard.quickAccess.amazon', iconName: 'ShoppingBag', category: 'integration' },
  { id: 'shopify', path: '/settings/shopify', labelKey: 'dashboard.quickAccess.shopify', iconName: 'ShoppingBag', category: 'integration' },
  { id: 'etsy', path: '/settings/etsy', labelKey: 'dashboard.quickAccess.etsy', iconName: 'Palette', category: 'integration' },
  { id: 'magento', path: '/settings/magento', labelKey: 'dashboard.quickAccess.magento', iconName: 'Globe', category: 'integration' },
  { id: 'woocommerce', path: '/settings/woocommerce', labelKey: 'dashboard.quickAccess.woocommerce', iconName: 'Globe', category: 'integration' },
  { id: 'shopware', path: '/settings/shopware', labelKey: 'dashboard.quickAccess.shopware', iconName: 'Globe', category: 'integration' },
  { id: 'dhl', path: '/settings/dhl', labelKey: 'dashboard.quickAccess.dhl', iconName: 'Truck', category: 'integration' },

  // Tools
  { id: 'repricing', path: '/repricing', labelKey: 'dashboard.quickAccess.repricing', iconName: 'TrendingUp', category: 'tools' },
  { id: 'banking', path: '/banking', labelKey: 'dashboard.quickAccess.banking', iconName: 'Landmark', category: 'tools' },
  { id: 'hazmat', path: '/articles/hazmat', labelKey: 'dashboard.quickAccess.hazmat', iconName: 'AlertTriangle', category: 'tools' },
  { id: 'import-export', path: '/articles?tab=import', labelKey: 'dashboard.quickAccess.importExport', iconName: 'ArrowUpDown', category: 'tools' },
  { id: 'manufacturers', path: '/settings/manufacturers', labelKey: 'dashboard.quickAccess.manufacturers', iconName: 'Factory', category: 'tools' },
];

interface QuickAccessConfig {
  selectedIds: string[];
}

const STORAGE_KEY = 'dashboard-quick-access';
const DEFAULT_IDS = ['articles-new', 'orders-new', 'inventory-receiving', 'inventory-stocktake'];
const MAX_TARGETS = 8;

function loadConfig(): QuickAccessConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to load quick access config:', e);
  }
  return null;
}

function saveConfig(config: QuickAccessConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to save quick access config:', e);
  }
}

export function useQuickAccessConfig() {
  const { hasPermission } = usePermission();
  const [config, setConfig] = useState<QuickAccessConfig>(() => {
    const stored = loadConfig();
    if (stored) {
      const validIds = stored.selectedIds.filter(id =>
        QUICK_ACCESS_TARGETS.some(t => t.id === id)
      );
      return { selectedIds: validIds.length > 0 ? validIds : DEFAULT_IDS };
    }
    return { selectedIds: DEFAULT_IDS };
  });

  /** Darf der Benutzer das Ziel öffnen? — ohne `requiredPermission` immer. */
  const darfZiel = (t: QuickAccessTarget): boolean => !t.requiredPermission
    || hasPermission(t.requiredPermission.module as PermissionModule, t.requiredPermission.operation as PermissionOperation);

  const selectedTargets = config.selectedIds
    .map(id => QUICK_ACCESS_TARGETS.find(t => t.id === id))
    .filter((t): t is QuickAccessTarget => t !== undefined && darfZiel(t));

  // Auch die Auswahl-Kennungen nur für sichtbare Ziele: Der Editor ordnet per Index um, und ein
  // unsichtbarer Eintrag davor verschöbe jeden Griff um eins.
  const sichtbareIds = selectedTargets.map(t => t.id);

  const allTargets = QUICK_ACCESS_TARGETS.filter(darfZiel);

  const targetsByCategory = (() => {
    const groups: Record<string, QuickAccessTarget[]> = {
      nav: [], create: [], integration: [], tools: [],
    };
    allTargets.forEach(t => groups[t.category].push(t));
    return groups;
  })();

  const addTarget = useCallback((id: string) => {
    setConfig(prev => {
      if (prev.selectedIds.length >= MAX_TARGETS) return prev;
      if (prev.selectedIds.includes(id)) return prev;
      const newConfig = { selectedIds: [...prev.selectedIds, id] };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  const removeTarget = useCallback((id: string) => {
    setConfig(prev => {
      const newConfig = { selectedIds: prev.selectedIds.filter(i => i !== id) };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  const reorderTargets = useCallback((newOrder: string[]) => {
    const newConfig = { selectedIds: newOrder };
    setConfig(newConfig);
    saveConfig(newConfig);
  }, []);

  const resetToDefaults = useCallback(() => {
    const newConfig = { selectedIds: DEFAULT_IDS };
    setConfig(newConfig);
    saveConfig(newConfig);
  }, []);

  const isSelected = useCallback((id: string) => {
    return config.selectedIds.includes(id);
  }, [config.selectedIds]);

  return {
    selectedTargets,
    selectedIds: sichtbareIds,
    allTargets,
    targetsByCategory,
    addTarget,
    removeTarget,
    reorderTargets,
    resetToDefaults,
    isSelected,
    maxTargets: MAX_TARGETS,
  };
}
